import { useState } from "react";

import { cn } from "@/utils/cn";

interface ImageGalleryProps {
  images: string[];
  alt: string;
}

/**
 * docs/ui/component-inventory.md `ImageGallery`/`ImageCarousel`: thumbnail
 * rail on desktop, dot indicators on mobile — one component, two trailing
 * navigation styles shown/hidden by breakpoint, both driving the same
 * `activeIndex` state.
 */
export function ImageGallery({ images, alt }: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (images.length === 0) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-lg bg-muted text-body-sm text-muted-foreground">
        No images
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="aspect-square w-full overflow-hidden rounded-lg bg-muted">
        <img src={images[activeIndex]} alt={alt} className="h-full w-full object-cover" />
      </div>

      {images.length > 1 && (
        <>
          <div className="hidden gap-2 sm:flex">
            {images.map((image, index) => (
              <button
                key={image}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={`View image ${index + 1} of ${images.length}`}
                aria-current={index === activeIndex}
                className={cn(
                  "h-16 w-16 overflow-hidden rounded-md border-2",
                  index === activeIndex ? "border-primary" : "border-transparent",
                )}
              >
                <img src={image} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>

          <div className="flex justify-center gap-1.5 sm:hidden">
            {images.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={`View image ${index + 1} of ${images.length}`}
                aria-current={index === activeIndex}
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  index === activeIndex ? "bg-primary" : "bg-border",
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
